import React, { useEffect, useMemo, useState } from 'react';
import { Volume2 } from 'lucide-react';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import catAlfabeto from '../assets/screens/cat-alfabeto.png';
import catNecessidades from '../assets/screens/cat-necessidades.png';
import cardA from '../assets/screens/card-a.png';
import cardB from '../assets/screens/card-b.png';
import cardC from '../assets/screens/card-c.png';
import cardTomarBanho from '../assets/screens/card-tomarbanho.png';
import cardFazerXixi from '../assets/screens/card-fazerxixi.png';
import cardEstouComFome from '../assets/screens/card-estoucomfome.png';

interface AACCard {
  image: string;
  label: string;
}

interface AACCategory {
  key: string;
  name: string;
  color: string;
  icon: string;
  cards: AACCard[];
}

// Mesmas cores e textos reais do app (ver colors.xml e strings.xml em
// Conectar/app/src/main/res) — nenhum dado inventado.
const categories: AACCategory[] = [
  {
    key: 'alfabeto',
    name: 'Alfabeto',
    color: '#03A9F4',
    icon: catAlfabeto,
    cards: [
      { image: cardA, label: 'Letra A' },
      { image: cardB, label: 'Letra B' },
      { image: cardC, label: 'Letra C' },
    ],
  },
  {
    key: 'necessidades',
    name: 'Necessidades',
    color: '#FF5CA7',
    icon: catNecessidades,
    cards: [
      { image: cardTomarBanho, label: 'Quero tomar banho' },
      { image: cardFazerXixi, label: 'Quero fazer xixi' },
      { image: cardEstouComFome, label: 'Estou com fome' },
    ],
  },
];

type Phase = 'tap' | 'voice' | 'pause';

interface Frame {
  categoryIndex: number;
  cardIndex: number;
  phase: Phase;
  duration: number;
}

// Passa pelos 3 cartões reais da categoria (devagar, dá tempo de ler) antes
// de trocar pra próxima categoria e repetir.
const CARD_SIZE = 168;
const CARD_GAP = 14;

function buildFrames(): Frame[] {
  const frames: Frame[] = [];
  categories.forEach((cat, categoryIndex) => {
    cat.cards.forEach((_, cardIndex) => {
      frames.push({ categoryIndex, cardIndex, phase: 'tap', duration: 400 });
      frames.push({ categoryIndex, cardIndex, phase: 'voice', duration: 2200 });
      frames.push({ categoryIndex, cardIndex, phase: 'pause', duration: 1400 });
    });
  });
  return frames;
}

/**
 * Simulação ao vivo de como o CAA funciona de verdade: cabeçalho da
 * categoria (nome + ícone reais) e, embaixo, a lista vertical de cartões
 * quadrados dela — deslizando de um pro outro, com o toque e a saída de voz
 * simulados — igual ao app (`fragment_alfabeto.xml`, `fragment_necessidades.xml`:
 * CardView 280dp x 280dp, 1 coluna). Depois dos cartões da categoria, troca
 * pra próxima e repete.
 */
export function CAALiveDemo() {
  const frames = useMemo(buildFrames, []);
  const prefersReducedMotion = usePrefersReducedMotion();
  const [frameIndex, setFrameIndex] = useState(0);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const frame = frames[frameIndex];
    const timer = setTimeout(() => {
      setFrameIndex((i) => (i + 1) % frames.length);
    }, frame.duration);
    return () => clearTimeout(timer);
  }, [frameIndex, frames, prefersReducedMotion]);

  // Parado no modo de movimento reduzido: mostra 1 cartão já "falando",
  // sem repetir o ciclo de toque.
  const current = prefersReducedMotion
    ? { categoryIndex: 0, cardIndex: 0, phase: 'voice' as Phase }
    : frames[frameIndex];
  const category = categories[current.categoryIndex];

  return (
    <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: category.color }} aria-hidden="true">
      {/* Cabeçalho — nome e ícone reais da categoria, como no topo do app */}
      <div className="flex items-center gap-2.5 px-4 py-3">
        <img src={category.icon} alt="" className="w-9 h-9 rounded-full flex-shrink-0" />
        <span className="text-white font-bold text-sm">{category.name}</span>
      </div>

      {/* Lista vertical (1 coluna, como no app) de cartões QUADRADOS —
          desliza pra cima até o cartão ativo, como o scroll real. */}
      <div
        className="relative overflow-hidden bg-black/10 mx-3 mb-3 rounded-xl"
        style={{ height: CARD_SIZE * 1.55 }}
      >
        <div
          className={`flex flex-col items-center ${prefersReducedMotion ? '' : 'transition-transform duration-700 ease-out'}`}
          style={{ transform: `translateY(-${current.cardIndex * (CARD_SIZE + CARD_GAP)}px)` }}
        >
          {category.cards.map((card, i) => {
            const isActive = i === current.cardIndex;
            const showTap = isActive && current.phase === 'tap';
            const showVoice = isActive && current.phase === 'voice';
            return (
              <div
                key={card.label}
                className={`relative mx-2 my-1.5 rounded-2xl bg-[#1C1C1E] flex flex-col items-center justify-center gap-2 p-3 transition-transform ${
                  showTap ? 'scale-95' : 'scale-100'
                }`}
                style={{ width: CARD_SIZE, height: CARD_SIZE }}
              >
                <img src={card.image} alt="" className="w-16 h-16 rounded-full flex-shrink-0" />
                <span className="text-white font-bold text-sm text-center leading-tight">{card.label}</span>

                {showVoice && (
                  <span className="absolute top-3 right-3 flex items-center justify-center w-7 h-7 rounded-full" style={{ backgroundColor: category.color }}>
                    {!prefersReducedMotion && (
                      <span
                        className="absolute inset-0 rounded-full animate-ping"
                        style={{ backgroundColor: category.color, opacity: 0.6 }}
                      />
                    )}
                    <Volume2 className="relative w-3.5 h-3.5 text-white" />
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
